import { cn } from "@/utils";
import { canvasToolbarIconClassName } from "../../components/toolbar/canvasToolbarIcon";
import {
  canvasToolbarColorSwatchClassName,
  canvasToolbarColorSwatchNoneClassName,
} from "../../components/toolbar/canvasToolbarStyles";
import type { ShapeStrokeStyle } from "../../hooks/useCanvasShapeAndEdgeStyle";

const STROKE_DASH: Record<
  Exclude<ShapeStrokeStyle, "none">,
  string | undefined
> = {
  solid: undefined,
  dashed: "4 2",
  dotted: "1 2",
};

/** Maps stored stroke widths to preview weights inside a 16×16 viewBox. */
const WEIGHT_PREVIEW: Record<number, number> = {
  1: 1,
  2: 1.5,
  4: 2.5,
};

interface CanvasToolbarColorSwatchProps {
  /** Inner fill — a soft, semi-transparent preview of the stroke hue. */
  fill: string;
  /** Ring color — the sticky tint / prior fill color. */
  stroke?: string;
  className?: string;
}

function isNoneSwatchFill(fill: string): boolean {
  return fill === "transparent" || fill === "none";
}

/** Circular color swatch — matches specs/canvas-toolbars ColorRow / `.toolbar-color-swatch`. */
export function CanvasToolbarColorSwatch({
  fill,
  stroke = "var(--n-90)",
  className,
}: CanvasToolbarColorSwatchProps) {
  if (isNoneSwatchFill(fill)) {
    return (
      <span
        className={cn(canvasToolbarColorSwatchNoneClassName, className)}
        aria-hidden
      />
    );
  }

  return (
    <span
      className={cn(canvasToolbarColorSwatchClassName, className)}
      style={{ background: fill, borderColor: stroke }}
      aria-hidden
    />
  );
}

interface CanvasStrokeTypePreviewProps {
  strokeStyle: ShapeStrokeStyle;
  active?: boolean;
  className?: string;
}

/** Horizontal stroke pattern preview — 16×16, matches other toolbar glyphs. */
export function CanvasStrokeTypePreview({
  strokeStyle,
  active = false,
  className,
}: CanvasStrokeTypePreviewProps) {
  const color = active ? "var(--rust)" : "var(--n-90)";

  if (strokeStyle === "none") {
    return (
      <svg
        className={cn(canvasToolbarIconClassName, className)}
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden
      >
        <line
          x1="2"
          y1="14"
          x2="14"
          y2="2"
          stroke="var(--n-30)"
          strokeWidth="1"
        />
      </svg>
    );
  }

  return (
    <svg
      className={cn(canvasToolbarIconClassName, className)}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <line
        x1="2"
        y1="8"
        x2="14"
        y2="8"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray={STROKE_DASH[strokeStyle]}
      />
    </svg>
  );
}

interface CanvasStrokeWidthPreviewProps {
  width: number;
  active?: boolean;
  className?: string;
}

/** Solid line weight preview — 16×16, matches other toolbar glyphs. */
export function CanvasStrokeWidthPreview({
  width,
  active = false,
  className,
}: CanvasStrokeWidthPreviewProps) {
  const color = active ? "var(--rust)" : "var(--n-90)";
  const previewWidth = WEIGHT_PREVIEW[width] ?? width;

  return (
    <svg
      className={cn(canvasToolbarIconClassName, className)}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <line
        x1="2"
        y1="8"
        x2="14"
        y2="8"
        stroke={color}
        strokeWidth={previewWidth}
        strokeLinecap="round"
      />
    </svg>
  );
}
